// NextAnniversaryCountdown — Hero Section Segment 2 (W3.1 §5.6).
//
// Reads from `GET /api/v1/anniversaries/next` (anchor priority, then
// days_until asc). Renders a 4px progress bar underneath the big number.

import { useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";

import { countdownLabelLong } from "@/lib/anniversary/countdown";
import { usePrefersReducedMotion } from "@/lib/anniversary/countUp";
import { progressBar } from "@/lib/anniversary/progressBar";
import { categoryLabelZh } from "@/lib/anniversary/categoryIcon";
import type { AnniversaryNextItem } from "@/lib/api/anniversary";
import { cn } from "@/lib/utils/cn";

export interface NextAnniversaryCountdownProps {
  item: AnniversaryNextItem | null;
  isLoading?: boolean;
}

export function NextAnniversaryCountdown({
  item,
  isLoading = false,
}: NextAnniversaryCountdownProps) {
  const nav = useNavigate();
  const reduced = usePrefersReducedMotion();

  if (isLoading) {
    return (
      <section
        aria-labelledby="hs-next-eyebrow"
        data-component="NextAnniversaryCountdown"
        data-tone="next"
        className="flex items-center gap-s-3 min-h-[96px] py-s-6 px-s-5 border-t border-hairline md:border-t-0 md:border-l md:border-hairline md:pl-s-5"
      >
        <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-you-tint text-you-strong animate-pulse" />
        <div className="flex-1 min-w-0 flex flex-col gap-1">
          <div className="h-3 w-20 rounded bg-you/10" />
          <div className="h-6 w-32 rounded bg-you/15" />
        </div>
      </section>
    );
  }

  if (!item) {
    return (
      <section
        aria-labelledby="hs-next-eyebrow"
        data-component="NextAnniversaryCountdown"
        data-tone="next"
        className="flex items-center gap-s-3 min-h-[96px] py-s-6 px-s-5 border-t border-hairline md:border-t-0 md:border-l md:border-hairline md:pl-s-5"
      >
        <span
          aria-hidden
          className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-you-tint text-you-strong"
        >
          <Sparkles size={22} strokeWidth={1.5} />
        </span>
        <div className="flex flex-col min-w-0 flex-1">
          <p
            id="hs-next-eyebrow"
            className="text-micro uppercase tracking-wider text-you-strong font-semibold"
          >
            還沒有下一個
          </p>
          <p className="text-[28px] font-display font-semibold leading-none text-ink-1">
            沒有未來紀念日
          </p>
          <p className="text-meta text-ink-2">點選右上角 + 新增一個</p>
        </div>
      </section>
    );
  }

  const bar = progressBar({
    isAnchor: item.is_anchor,
    isRecurring: true, // /next always surfaces recurring rows
    nextOccurrenceIso: item.next_occurrence_date,
  });

  const handleActivate = () => nav(`/anniversaries/${item.id}`);

  return (
    <section
      aria-labelledby="hs-next-eyebrow"
      data-component="NextAnniversaryCountdown"
      data-tone="next"
      className={cn(
        "flex items-center gap-s-3 min-h-[96px] py-s-6 px-s-5",
        "border-t border-hairline md:border-t-0 md:border-l md:border-hairline md:pl-s-5",
      )}
    >
      <span
        aria-hidden
        className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-you-tint text-you-strong"
      >
        <Sparkles size={22} strokeWidth={1.5} />
      </span>
      <div
        role="button"
        tabIndex={0}
        onClick={handleActivate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleActivate();
          }
        }}
        aria-label={`下一個紀念日 ${item.title} ${countdownLabelLong(item.days_until)}`}
        data-testid="next-anniversary-tile"
        className={cn(
          "flex-1 min-w-0 flex flex-col gap-s-2 cursor-pointer press",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2 rounded-md",
          !reduced && "transition-transform duration-quick ease-paper active:scale-[0.98]",
        )}
      >
        <p
          id="hs-next-eyebrow"
          className="text-micro uppercase tracking-wider text-you-strong font-semibold"
        >
          {item.is_anchor ? "下一個 · 交往紀念日" : `下一個 · ${categoryLabelZh(item.category)}`}
        </p>
        <div className="flex items-baseline gap-2">
          <p
            className="text-[28px] font-display font-semibold leading-none text-ink-1 tabular-nums"
            data-testid="next-anniversary-value"
          >
            {countdownLabelLong(item.days_until)}
          </p>
          <span className="text-micro font-mono text-ink-2" data-testid="next-anniversary-percent">
            {bar.percent}%
          </span>
        </div>
        <p className="text-meta text-ink-2 truncate">
          {item.title} · {item.next_occurrence_date}
        </p>
        <div
          className="h-1 w-full rounded-pill bg-you/10 overflow-hidden"
          aria-hidden
          data-testid="next-anniversary-progress"
        >
          <div
            className="h-full bg-you-strong rounded-pill"
            style={{
              width: `${bar.percent}%`,
              transition: reduced ? "none" : "width var(--dur-slow) ease-quiet",
            }}
          />
        </div>
      </div>
    </section>
  );
}